// Decoder for the impact analysis-recommendation payload (contract 03 §6).
//
// Lenient by design, same reasoning as module 23's: v3 read this payload with bare optional
// chaining and rendered whatever arrived, so a strict decoder would blank the whole section over
// one malformed frame. A frame survives if it has a `recommendation_id`; everything else degrades
// to a neutral value, and the SERVICE decides whether a frame is runnable (it needs a run payload
// with a profile slug and a date range — see `impactRecommendations.frameToPrefill`).
//
// NOT decoded: `pending_segments` / `pending_days` / `scan_window_days`. v3 normalised all three
// through a five-deep fallback chain and then rendered NONE of them — `pendingText` and
// `scanWindowDays` are computed in `ImpactAnalysisRecommendationStrip.vue` and referenced by no
// template. Porting them would ship dead code that no lint catches. `not_enough_data` and
// `warnings` DO stay: the first is part of the run-identity fallback chain (see the service's
// `sectionIdentity`), the second is what v3's pending note was mined from.
import type {
  ImpactRecommendationExecution,
  ImpactRecommendationFrame,
  RecommendationMeasurement,
  RecommendationRunPayload,
} from './recommendation.types'

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function numeric(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []
}

function measurement(value: unknown): RecommendationMeasurement {
  const row = asRecord(value)
  if (!row) return {}
  return {
    ...(text(row.target_metric) === null
      ? {}
      : { target_metric: text(row.target_metric) as string }),
    ...(Array.isArray(row.control_metrics)
      ? { control_metrics: stringList(row.control_metrics) }
      : {}),
    pre_days: numeric(row.pre_days),
    post_days: numeric(row.post_days),
    min_clicks_total: numeric(row.min_clicks_total),
    ...(Array.isArray(row.group_by) ? { group_by: stringList(row.group_by) } : {}),
  }
}

/** `null` when the payload cannot describe a run at all; the service treats that as unrunnable. */
function runPayload(value: unknown): RecommendationRunPayload | null {
  const row = asRecord(value)
  if (!row) return null
  const slugs = stringList(row.profile_slugs)
  const parameters = asRecord(row.input_parameters) ?? {}
  return {
    ...row,
    name: text(row.name) ?? '',
    profile_slugs: slugs,
    input_parameters: parameters,
    ...(text(row.frequency) === null ? {} : { frequency: text(row.frequency) as string }),
    max_credits: numeric(row.max_credits),
  }
}

function decodeFrame(value: unknown): ImpactRecommendationFrame | null {
  const row = asRecord(value)
  if (!row) return null
  const rawId = row.recommendation_id
  if (typeof rawId !== 'string' && typeof rawId !== 'number') return null
  const recommendationId = String(rawId)
  if (recommendationId === '') return null

  return {
    recommendation_id: recommendationId,
    rank: numeric(row.rank),
    platform: text(row.platform),
    change_type_label: text(row.change_type_label),
    profile_name: text(row.profile_name),
    title: text(row.title),
    rationale_html: text(row.rationale_html),
    eligible_segment_count: numeric(row.eligible_segment_count),
    affected_spend: numeric(row.affected_spend),
    estimated_credits: numeric(row.estimated_credits),
    measurement: measurement(row.measurement),
    run_payload: runPayload(row.run_payload),
  }
}

/** The backend wraps this endpoint's payload in `{ result: … }` inconsistently — v3 read
 *  `res.data?.result ?? res.data`, and that unwrap lives here so both endpoints share one rule. */
export function decodeImpactRecommendationExecution(value: unknown): ImpactRecommendationExecution {
  const envelope = asRecord(value)
  const body = (envelope && asRecord(envelope.result)) ?? envelope
  if (!body) throw new TypeError('impact recommendation execution must be an object')

  const rows = Array.isArray(body.recommendations) ? body.recommendations : []
  const recommendations: ImpactRecommendationFrame[] = []
  for (const row of rows) {
    const frame = decodeFrame(row)
    if (frame) recommendations.push(frame)
  }

  return {
    execution_id: text(body.execution_id),
    generated_at: text(body.generated_at),
    completed_at: text(body.completed_at),
    date_from: text(body.date_from),
    date_to: text(body.date_to),
    not_enough_data: body.not_enough_data === true,
    warnings: stringList(body.warnings),
    recommendations,
  }
}
